import { TabType } from './types';
import {
  OnboardingStepStatus,
  OnboardingStep,
} from 'further-types/lp/onboarding';
import { useState } from 'react';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { useOnboardingOverviewNavigationContext } from '../OnboardingOverviewNavigationContext';

const manualCommitmentFlowTabs = [
  {
    label: 'Commitment details',
    key: TabType.CommitmentDetails,
  },
  {
    label: 'Investor details',
    key: TabType.InvestorDetails,
  },
  {
    label: 'Communications',
    key: TabType.Communications,
  },
  {
    label: 'Notes',
    key: TabType.Notes,
  },
];

const standardTabs = [
  {
    label: 'Investor details',
    key: TabType.InvestorDetails,
  },
  {
    label: 'Investor journey',
    key: TabType.InvestorJourney,
  },
  {
    label: 'Communications',
    key: TabType.Communications,
  },
  {
    label: 'Notes',
    key: TabType.Notes,
  },
];

export default function useViewInvestor({
  vehicleApplicationId,
  vehicleId,
  onClose,
}: {
  vehicleApplicationId: string;
  vehicleId: string;
  onClose: () => void;
}) {
  const {
    showCompleteCommitmentFrom,
    selectedVehicleApplicationTab,
    goToVehicleApplication,
  } = useOnboardingOverviewNavigationContext();
  const [completeCommitmentFormVisible, setCompleteCommitmentFormVisible] =
    useState(showCompleteCommitmentFrom);

  const { vehicleApplication, users, disableManualCommitmentFlow } =
    useVehicleApplication(vehicleId, vehicleApplicationId);
  const hasCommitment = !!vehicleApplication.commitment;
  const hasNoCommitment = !hasCommitment;

  const handleDeleteCommitment = () => {
    disableManualCommitmentFlow({
      vehicleId,
      vehicleApplicationId,
    });
  };

  const isManualCommitmentFlow =
    vehicleApplication.commitmentSettings.isManuallyAddedCommitment;

  const visibleTabs = isManualCommitmentFlow
    ? manualCommitmentFlowTabs
    : standardTabs;

  const selectedTab = !visibleTabs.some(
    (t) => t.key === selectedVehicleApplicationTab,
  )
    ? visibleTabs[0].key
    : selectedVehicleApplicationTab;

  const handleChangeTab = (tab: TabType) => {
    goToVehicleApplication(vehicleApplicationId, tab);
  };

  const currentTabIndex = visibleTabs.findIndex(
    (tab) => selectedTab === tab.key,
  );
  const isLastTab = currentTabIndex + 1 === visibleTabs.length;
  const isFirstTab = currentTabIndex === 0;

  const handleGoToNextTab = () => {
    if (isLastTab) {
      return;
    }

    goToVehicleApplication(
      vehicleApplicationId,
      visibleTabs[currentTabIndex + 1].key,
    );
  };

  const handleGoToPreviousTab = () => {
    if (isFirstTab) {
      onClose();
      return;
    }

    goToVehicleApplication(
      vehicleApplicationId,
      visibleTabs[currentTabIndex - 1].key,
    );
  };

  const showCompleteCommitmentButton =
    hasNoCommitment && selectedTab === TabType.InvestorJourney;

  const showCommitmentSettingsButtons =
    isManualCommitmentFlow &&
    hasNoCommitment &&
    selectedTab === TabType.CommitmentDetails;

  const hasActiveRequests = vehicleApplication.applicationSteps
    .filter(
      (step) =>
        step.step !== OnboardingStep.InvestorPortalView &&
        step.step !== OnboardingStep.DataRoomView,
    )
    .some((step) => step.status !== OnboardingStepStatus.Disabled);
  const hasPendingRequests = vehicleApplication.applicationSteps.some(
    (step) => step.status === OnboardingStepStatus.Requested,
  );
  const hasUsersAcceptingEmailsWithoutFirstInvite = users.some(
    (user) => user.copyIntoSystemEmails && !user.firstInvite,
  );
  const showSystemEmailsBanner =
    !isManualCommitmentFlow &&
    ((hasActiveRequests && hasPendingRequests) || hasCommitment) &&
    hasUsersAcceptingEmailsWithoutFirstInvite;

  return {
    completeCommitmentFormVisible,
    showCompleteCommitmentForm: () => {
      setCompleteCommitmentFormVisible(true);
    },
    hideCompleteCommitmentForm: () => {
      setCompleteCommitmentFormVisible(false);
    },
    visibleTabs,
    selectedTab: selectedTab ?? visibleTabs[0].key,
    showCommitmentSettingsButtons,
    showCompleteCommitmentButton,
    handleDeleteCommitment,
    handleGoToNextTab,
    handleGoToPreviousTab,
    handleChangeTab,
    isLastTab,
    isFirstTab,
    showSystemEmailsBanner,
  };
}
